import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faSquareCheck, faSquareMinus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { cssVar } from "../utils/cssVar";

export type SelectionToolbarProps = {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
};

/**
 * Selection summary + actions shown above the files: "{n} selected" and a
 * two-way control rendered as an icon plus a text label that stay in sync.
 *
 * Both the icon and the text label are the same two-way control:
 *  - not everything selected → `square-minus` + "Select all files" → selects all,
 *  - everything selected → checked checkbox + "Clear selection" → clears.
 *
 * It is always mounted (toggled via `visibility`) so it reserves a precise,
 * constant slot — the controls only appear once something is selected, and the
 * grid never shifts (which would invalidate the marquee's cached rects). While
 * hidden it is also removed from the tab order and the accessibility tree.
 *
 * The XD spec uses blue; per product direction the live UI uses the primary
 * theme color instead.
 */
export function SelectionToolbar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
}: SelectionToolbarProps) {
  const { t } = useTranslation();
  const primaryColor = cssVar("--primary-color");
  const isVisible = selectedCount > 0;
  const allSelected = selectedCount > 0 && selectedCount === totalCount;
  const toggleLabel = allSelected
    ? t("Clear selection")
    : t("Select all files");

  return (
    <div
      className="flex items-center gap-3 pb-3"
      style={{ visibility: isVisible ? "visible" : "hidden" }}
      aria-hidden={!isVisible}
    >
      <span className="text-[14px] font-medium leading-[18px] text-[#999999]">
        {t("{{count}} selected", { count: selectedCount })}
      </span>
      <Tooltip title={toggleLabel} placement="top">
        <button
          type="button"
          onClick={allSelected ? onClearSelection : onSelectAll}
          aria-label={toggleLabel}
          className="flex items-center outline-none"
        >
          <FontAwesomeIcon
            icon={(allSelected ? faSquareCheck : faSquareMinus) as IconProp}
            style={{ color: primaryColor, fontSize: 16 }}
          />
        </button>
      </Tooltip>
      <button
        type="button"
        onClick={allSelected ? onClearSelection : onSelectAll}
        className="text-[14px] font-medium leading-[22px] outline-none"
        style={{ color: primaryColor }}
      >
        {toggleLabel}
      </button>
    </div>
  );
}
